import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';

export interface GamificationStatus {
    xp: {
        total: number;
        level: number;
        next_level_xp: number;
        level_title: string;
    };
    streak: {
        current: number;
        best: number;
        multiplier: number;
        last_activity: string | null;
    };
    stats: {
        tasks_completed: number;
        referrals: number;
        credits_earned: number;
    };
    badges: string[];
    tasks: UserTask[];
}

export interface UserTask {
    id: string;
    task_type: string;
    title: string;
    description: string;
    icon: string;
    xp_reward: number;
    credit_reward: number;
    status: 'available' | 'claimed' | 'completed';
    progress: number;
    progress_max: number;
}

export interface TaskType {
    id: string;
    slug: string;
    title: string;
    description: string;
    category: 'onboarding' | 'daily' | 'referral' | 'engagement' | 'general';
    xp_reward: number;
    credit_reward: number;
    icon: string;
    max_per_day: number | null;
    max_lifetime: number | null;
}

interface TaskResult {
    success: boolean;
    error?: string;
    xp_earned?: number;
    credits_earned?: number;
    multiplier?: number;
    leveled_up?: boolean;
    new_level?: number;
}

interface StreakResult {
    success: boolean;
    current_streak: number;
    best_streak: number;
    multiplier: number;
    streak_increased?: boolean;
    streak_broken?: boolean;
    already_logged?: boolean;
}

export function useGamification() {
    const { user } = useAuth();
    const [status, setStatus] = useState<GamificationStatus | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // Fetch gamification status
    const fetchStatus = useCallback(async () => {
        if (!user) {
            setStatus(null);
            setLoading(false);
            return;
        }

        try {
            const { data, error: fnError } = await supabase.functions.invoke('gamification', {
                body: { action: 'get_status' },
            });

            if (fnError) throw fnError;
            setStatus(data);
            setError(null);
        } catch (err) {
            console.error('[useGamification] Error fetching status:', err);
            setError('Erro ao carregar gamificação');
        } finally {
            setLoading(false);
        }
    }, [user]);

    // Update streak on mount (login/app open)
    const updateStreak = useCallback(async (): Promise<StreakResult | null> => {
        if (!user) return null;

        try {
            const { data, error: fnError } = await supabase.functions.invoke('gamification', {
                body: { action: 'update_streak' },
            });

            if (fnError) throw fnError;

            // Refresh status after streak update
            await fetchStatus();

            return data as StreakResult;
        } catch (err) {
            console.error('[useGamification] Error updating streak:', err);
            return null;
        }
    }, [user, fetchStatus]);

    // Claim a task
    const claimTask = useCallback(async (taskSlug: string): Promise<TaskResult> => {
        if (!user) return { success: false, error: 'Not authenticated' };

        try {
            const { data, error: fnError } = await supabase.functions.invoke('gamification', {
                body: { action: 'claim_task', taskSlug },
            });

            if (fnError) throw fnError;

            // Refresh status
            await fetchStatus();

            return data as TaskResult;
        } catch (err) {
            console.error('[useGamification] Error claiming task:', err);
            return { success: false, error: 'Erro ao reivindicar task' };
        }
    }, [user, fetchStatus]);

    // Complete a task
    const completeTask = useCallback(async (
        taskSlug: string,
        metadata?: Record<string, unknown>
    ): Promise<TaskResult> => {
        if (!user) return { success: false, error: 'Not authenticated' };

        try {
            const { data, error: fnError } = await supabase.functions.invoke('gamification', {
                body: { action: 'complete_task', taskSlug, metadata },
            });

            if (fnError) throw fnError;

            // Refresh status
            await fetchStatus();

            return data as TaskResult;
        } catch (err) {
            console.error('[useGamification] Error completing task:', err);
            return { success: false, error: 'Erro ao completar task' };
        }
    }, [user, fetchStatus]);

    // Initial fetch
    useEffect(() => {
        if (user) {
            fetchStatus();
            // Update streak on app open
            updateStreak();
        } else {
            setStatus(null);
            setLoading(false);
        }
    }, [user, fetchStatus, updateStreak]);

    // Calculate XP progress to next level
    const xpProgress = status ? {
        current: status.xp.total,
        nextLevel: status.xp.next_level_xp,
        percentage: status.xp.next_level_xp
            ? Math.min(100, (status.xp.total / status.xp.next_level_xp) * 100)
            : 100,
    } : null;

    return {
        status,
        loading,
        error,
        xpProgress,
        fetchStatus,
        updateStreak,
        claimTask,
        completeTask,
    };
}
